
/**
 * Module dependencies.
 */

import { authRoutes, myRevigresRoutes } from 'src/core/routes';
import { color, media, units } from 'src/styles/utils';
import { createToken } from 'src/api/auth/create-token';
import { getNetworkErrorTranslatedKey } from 'src/core/utils/errors';
import { regexes } from 'src/core/utils/regexes';
import { serverAuth } from 'src/core/utils/session';
import { theme } from 'styled-tools';
import { useAuth } from 'src/context/auth/context';
import { useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import { useRouter } from 'next/router';
import { useSnackbar } from 'src/context/snackbar/context';
import { useTranslate } from 'src/core/utils/translator';
import AuthBaseLayout from 'src/components/layout/auth-base-layout';
import Button from 'src/components/core/buttons/button';
import Input from 'src/components/core/forms/fields/input';
import Metatags from 'src/components/metatags';
import Password from 'src/components/core/forms/fields/password';
import RawHtml from 'src/components/core/raw-html';
import React, { ReactElement, useCallback } from 'react';
import RouterLink from 'src/components/core/links/router-link';
import Trans from 'next-translate/Trans';
import Type from 'src/components/core/typography';
import styled from 'styled-components';
import useNavbarTheme from 'src/context/navbar-theme/use-navbar-theme';

/**
 * `FormData` type.
 */

type FormData = {
  email: string,
  password: string
};

/**
 * `StyledParagraph` styled component.
 */

const StyledParagraph = styled(Type.Paragraph)`
  color: ${color('grey400')};
  display: flex;
  flex-direction: column;
  margin-bottom: ${units(6)};

  ${media.min('xs')`
    flex-direction: row;
  `}
`;

/**
 * `Link` styled component.
 */

const Link = styled(RouterLink)`
  color: ${color('grey400')};
  cursor: pointer;
  display: table;
  text-decoration: underline;
  transition: color, ${theme('animations.defaultTransition')};
  width: max-content;

  &:focus,
  &:hover {
    color: ${color.transparentize('grey400', 0.8)};
  }

  ${media.min('xs')`
    margin-left: ${units(0.5)};
  `}
`;

/**
 * `ButtonsWrapper` styled component.
 */

const ButtonsWrapper = styled.div`
  display: grid;
  grid-gap: ${units(3)};
  grid-template-columns: max-content;
`;

/**
 * `RecoverPasswordLink` component.
 */

const RecoverPasswordLink = ({ children }: { children?: string }) => {
  const { locale } = useRouter();

  return (
    <Link
      href={authRoutes.recoverPassword}
      locale={locale}
    >
      {children}
    </Link>
  );
};

/**
 * `useSignIn` hook.
 */

function useSignIn() {
  const router = useRouter();
  const { translate } = useTranslate();
  const { showMessage } = useSnackbar();
  const { onUpdateToken } = useAuth();
  const { isLoading, mutate } = useMutation(createToken);
  const handleSignIn = useCallback((formData: FormData) => {
    mutate(formData, {
      onError: ({ data, status }) => {
        const code = status === 404 ? 'not_found' : data?.code;
        const translatedKey = getNetworkErrorTranslatedKey('signIn', code);

        showMessage(translate(`authentication:${translatedKey}`), {
          appearance: 'error',
          duration: 3500
        });

        if (status === 404) {
          router.push(authRoutes.signUp);
        }
      },
      onSuccess: ({ token }) => {
        onUpdateToken(token);

        showMessage(translate('authentication:signIn.successMessage'), {
          appearance: 'success'
        });

        router.push(myRevigresRoutes.home);
      }
    });
  }, [mutate, onUpdateToken, router, showMessage, translate]);

  return {
    isLoading,
    signIn: handleSignIn
  };
}

/**
 * `SignIn` page.
 */

const SignIn = (): ReactElement => {
  const { locale, translate } = useTranslate();
  const {
    errors,
    formState,
    handleSubmit,
    register
  } = useForm({ mode: 'onBlur' });

  const { isSubmitting } = formState;
  const { isLoading: isLoadingSignIn, signIn } = useSignIn();
  const isLoading = isLoadingSignIn || isSubmitting;
  const onSubmit = useCallback(formData => {
    signIn(formData);
  }, [signIn]);

  const description = translate('authentication:signIn.description');
  const title = translate('authentication:signIn.title');

  useNavbarTheme({ theme: 'dark' });

  return (
    <>
      <Metatags
        description={description}
        pageTitle={title}
      />

      <AuthBaseLayout
        description={
          <RawHtml>
            {description}
          </RawHtml>
        }
        title={title}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <Input
            disabled={isLoading}
            error={errors?.email}
            label={translate('forms:emailLabel')}
            name={'email'}
            placeholder={translate('forms:emailPlaceholder')}
            ref={register<any>({
              pattern: {
                message: translate('forms:emailPatternValidation'),
                value: regexes.email
              },
              required: {
                message: translate('forms:requiredField'),
                value: true
              }
            })}
            type={'email'}
          />

          <Password
            disabled={isLoading}
            error={errors?.password}
            label={translate('forms:passwordLabel')}
            name={'password'}
            placeholder={translate('forms:passwordPlaceholder')}
            ref={register<any>({
              required: {
                message: translate('forms:requiredField'),
                value: true
              }
            })}
          />

          <StyledParagraph>
            <Trans
              components={[<RecoverPasswordLink key={'recover'} />]}
              i18nKey={'authentication:signIn.recoverPassword'}
            />
          </StyledParagraph>

          <ButtonsWrapper>
            <Button
              colorTheme={'primary'}
              isLoading={isLoading}
              type={'submit'}
              variant={'fill'}
            >
              {translate('authentication:signIn.submitButton')}
            </Button>

            <Button
              disabled={isLoading}
              href={authRoutes.signUp}
              locale={locale}
              variant={'outlined'}
            >
              {translate('authentication:signUp.submitButton')}
            </Button>
          </ButtonsWrapper>
        </form>
      </AuthBaseLayout>
    </>
  );
};

/**
 * Export `getServerSideProps`.
 */

export async function getServerSideProps(ctx) {
  const { isAuthenticated } = await serverAuth(ctx);

  if (isAuthenticated) {
    return {
      redirect: {
        destination: myRevigresRoutes.home,
        permanent: false
      }
    };
  }

  return {
    props: {}
  };
}

/**
 * Export `SignIn` page.
 */

export default SignIn;
